'use client';

import { useEffect, useState } from 'react';
import { X, ShoppingCart, Check, Minus, Plus, Package } from 'lucide-react';
import useSepetStore from '@/store/sepet.store';
import useSiteStore from '@/store/site.store';
import { API_URL } from '@/lib/config';
import toast from 'react-hot-toast';

const RESIM_BASE = API_URL.replace('/api', '');

interface Varyasyon {
  id: number;
  varyasyon_ad: string;
  satis_fiyati: number;
  piyasa_fiyati: number;
  stok: number;
  aktif?: boolean;
  konfigurasyon_json?: string | null;
}

interface Props {
  acik: boolean;
  onKapat: () => void;
  urun_id: number;
  urun_ad: string;
  urun_kdv: number;
  resimler?: { dosya_yolu: string; alt_metin?: string | null }[];
  varyasyonlar: Varyasyon[];
}

export default function SepeteEkleModal({
  acik, onKapat,
  urun_id, urun_ad, urun_kdv,
  resimler = [], varyasyonlar,
}: Props) {
  const { ekle } = useSepetStore();
  const satis_kapali = useSiteStore(s => s.satis_kapali);

  const [seciliVaryasyon, setSeciliVaryasyon] = useState<Varyasyon | null>(null);
  const [miktar, setMiktar] = useState(1);
  const [eklendi, setEklendi] = useState(false);

  // Modal açılınca ilk aktif varyasyonu seç
  useEffect(() => {
    if (!acik) return;
    setMiktar(1);
    setEklendi(false);
    const aktif = varyasyonlar.find(v => v.aktif !== false && v.stok > 0)
      ?? varyasyonlar.find(v => v.aktif !== false)
      ?? varyasyonlar[0]
      ?? null;
    setSeciliVaryasyon(aktif);
  }, [acik, varyasyonlar]);

  const sepeteEkle = () => {
    if (!seciliVaryasyon) return;
    const mevcutMiktar = useSepetStore.getState().kalemler.find(k => k.varyasyon_id === seciliVaryasyon.id)?.miktar ?? 0;
    if (mevcutMiktar + miktar > seciliVaryasyon.stok) {
      toast.error(`Stokta sadece ${seciliVaryasyon.stok} adet var!`);
      return;
    }
    ekle(seciliVaryasyon.id, miktar);
    setEklendi(true);
    toast.success(`${miktar} ürün sepete eklendi!`);
    setTimeout(() => { setEklendi(false); onKapat(); }, 1500);
  };

  if (!acik) return null;

  const fiyatVar = seciliVaryasyon && Number(seciliVaryasyon.satis_fiyati) > 0;
  const kdvDahil = fiyatVar ? Math.ceil(Number(seciliVaryasyon!.satis_fiyati) * (1 + urun_kdv / 100)) : 0;
  const stokBitti = seciliVaryasyon ? seciliVaryasyon.stok === 0 : false;
  const ilkResim = resimler[0]?.dosya_yolu ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onKapat} />

      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
        {/* Başlık */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-gray-100)]">
          <h3 className="text-sm font-bold text-[var(--color-gray-800)] truncate pr-4">{urun_ad}</h3>
          <button onClick={onKapat} className="p-1.5 hover:bg-[var(--color-gray-100)] rounded-full transition flex-shrink-0">
            <X className="w-4 h-4 text-[var(--color-gray-500)]" />
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Resim + Fiyat */}
          <div className="flex gap-3">
            <div className="w-20 h-20 rounded-[var(--radius-lg)] overflow-hidden bg-[var(--color-gray-100)] flex-shrink-0">
              {ilkResim ? (
                <img src={`${RESIM_BASE}${ilkResim}`} alt={urun_ad} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-8 h-8 text-[var(--color-gray-300)]" />
                </div>
              )}
            </div>
            <div className="flex-1">
              {seciliVaryasyon ? (
                fiyatVar ? (
                  <>
                    <p className="text-2xl font-bold text-[var(--color-primary)]">
                      {Number(seciliVaryasyon.satis_fiyati).toLocaleString('tr-TR')} ₺
                    </p>
                    <p className="text-xs text-[var(--color-gray-400)]">KDV dahil: {kdvDahil.toLocaleString('tr-TR')} ₺</p>
                    {stokBitti
                      ? <p className="text-xs text-[var(--color-error)] mt-1 font-medium">Stok Tükendi</p>
                      : <p className="text-xs text-[var(--color-success)] mt-1">✓ Stokta: {seciliVaryasyon.stok} adet</p>
                    }
                  </>
                ) : (
                  <p className="text-sm text-[var(--color-gray-500)] font-medium">Fiyat Sorunuz</p>
                )
              ) : (
                <p className="text-sm text-[var(--color-gray-400)]">Seçenek belirleyin</p>
              )}
            </div>
          </div>

          {/* Varyasyon listesi */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-[var(--color-gray-600)]">Seçenek</p>
            {varyasyonlar.map(v => (
              <button
                key={v.id}
                onClick={() => v.stok > 0 && setSeciliVaryasyon(v)}
                disabled={v.stok === 0}
                className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-[var(--radius-lg)] text-sm transition ${
                  seciliVaryasyon?.id === v.id
                    ? 'border-[var(--color-primary)] bg-[var(--color-info-light)] text-[var(--color-primary)]'
                    : v.stok === 0
                    ? 'border-[var(--color-gray-200)] text-[var(--color-gray-300)] cursor-not-allowed'
                    : 'border-[var(--color-gray-200)] text-[var(--color-gray-700)] hover:border-[var(--color-primary)]'
                }`}
              >
                <span className={`font-medium ${v.stok === 0 ? 'line-through' : ''}`}>{v.varyasyon_ad}</span>
                <div className="flex items-center gap-2">
                  {Number(v.satis_fiyati) > 0 && (
                    <span className={`text-sm font-bold ${seciliVaryasyon?.id === v.id ? 'text-[var(--color-primary)]' : 'text-[var(--color-gray-700)]'}`}>
                      {Number(v.satis_fiyati).toLocaleString('tr-TR')} ₺
                    </span>
                  )}
                  {v.stok === 0 && <span className="text-xs text-[var(--color-error)]">Tükendi</span>}
                  {seciliVaryasyon?.id === v.id && v.stok > 0 && (
                    <Check className="w-4 h-4 text-[var(--color-primary)]" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Adet */}
          {fiyatVar && !stokBitti && (
            <div className="flex items-center gap-3">
              <p className="text-xs font-semibold text-[var(--color-gray-600)]">Adet:</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setMiktar(m => Math.max(1, m - 1))}
                  className="w-7 h-7 border border-[var(--color-gray-300)] rounded-[var(--radius-md)] flex items-center justify-center hover:bg-[var(--color-gray-100)] transition">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-8 text-center font-bold">{miktar}</span>
                <button onClick={() => {
                    const mevcutMiktar = useSepetStore.getState().kalemler.find(k => k.varyasyon_id === seciliVaryasyon?.id)?.miktar ?? 0;
                    if (miktar + mevcutMiktar >= (seciliVaryasyon?.stok ?? 1)) {
                      toast.error(`En fazla ${seciliVaryasyon?.stok} adet ekleyebilirsiniz!`);
                      return;
                    }
                    setMiktar(m => m + 1);
                  }}
                  className="w-7 h-7 border border-[var(--color-gray-300)] rounded-[var(--radius-md)] flex items-center justify-center hover:bg-[var(--color-gray-100)] transition">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sepete Ekle */}
        <div className="px-4 pb-4 pt-2 border-t border-[var(--color-gray-100)]">
          {satis_kapali ? (
            <button disabled className="w-full py-3 bg-[var(--color-gray-200)] text-[var(--color-gray-500)] rounded-[var(--radius-lg)] text-sm font-semibold">
              Fiyat Sorunuz
            </button>
          ) : (
            <button
              onClick={sepeteEkle}
              disabled={!seciliVaryasyon || stokBitti || eklendi}
              className={`w-full py-3 rounded-[var(--radius-lg)] text-sm font-semibold flex items-center justify-center gap-2 transition ${
                eklendi
                  ? 'bg-[var(--color-success)] text-white'
                  : !seciliVaryasyon || stokBitti
                  ? 'bg-[var(--color-gray-200)] text-[var(--color-gray-400)] cursor-not-allowed'
                  : 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-dark)]'
              }`}
            >
              {eklendi
                ? <><Check className="w-4 h-4" /> Sepete Eklendi!</>
                : <><ShoppingCart className="w-4 h-4" /> Sepete Ekle</>
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
